import HttpService from "./httpService";

// Cached VAPID public key fetched from backend
let cachedVapidPublicKey: string | null = null;

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

class NotificationService {
  private swRegistration: ServiceWorkerRegistration | null = null;
  private isSupported: boolean;

  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  async initialize(): Promise<boolean> {
    if (!this.isSupported) {
      console.warn('Push notifications are not supported in this browser');
      return false;
    }

    try {
      const [registration, vapidResponse] = await Promise.all([
        this.resolveServiceWorkerRegistration(),
        this.fetchVapidPublicKey()
      ]);
      this.swRegistration = registration;
      cachedVapidPublicKey = vapidResponse;
      console.log('Service Worker ready');
      return true;
    } catch (error) {
      console.error('Service Worker not ready:', error);
      return false;
    }
  }

  private async resolveServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
    // Fast path: if there's already an active SW, use it immediately
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing?.active) {
      return existing;
    }

    // If the SW is installing or waiting, listen for state changes
    const swToWatch = existing?.installing || existing?.waiting;
    if (swToWatch) {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Service Worker took too long to activate. Please refresh and try again.')), 30000);
        swToWatch.addEventListener('statechange', function handler() {
          if (swToWatch.state === 'activated') {
            clearTimeout(timer);
            swToWatch.removeEventListener('statechange', handler);
            resolve();
          }
        });
      });
      const reg = await navigator.serviceWorker.getRegistration('/');
      if (reg?.active) return reg;
    }

    // Final fallback: wait for ready (handles fresh install where registration hasn't fired yet)
    return Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Service Worker took too long to activate. Please refresh and try again.')), 30000)
      )
    ]);
  }

  private async fetchVapidPublicKey(): Promise<string> {
    if (cachedVapidPublicKey) return cachedVapidPublicKey;

    try {
      const response = await HttpService.get('/push-notifications/vapid-public-key') as any;
      const key = response?.data?.publicKey || response?.publicKey;
      if (key) return key;
    } catch (error) {
      console.warn('Failed to fetch VAPID public key from server, falling back to env config', error);
    }

    const envKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (envKey) return envKey;

    throw new Error('Failed to fetch VAPID public key from server and env');
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported');
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  async getPermissionStatus(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      return 'denied';
    }
    return Notification.permission;
  }

  async subscribeToPushNotifications(): Promise<PushSubscription | null> {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported in this browser');
    }

    // Lazily resolve registration if initialize() hasn't completed yet
    if (!this.swRegistration) {
      this.swRegistration = await this.resolveServiceWorkerRegistration();
    }

    const permission = await this.getPermissionStatus();
    if (permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    const vapidKey = cachedVapidPublicKey || await this.fetchVapidPublicKey();
    cachedVapidPublicKey = vapidKey;
    const applicationServerKey = this.urlBase64ToUint8Array(vapidKey);

    try {
      // If there's an existing subscription with a different key, unsubscribe first
      const existingSubscription = await this.swRegistration.pushManager.getSubscription();
      if (existingSubscription) {
        const existingKey = existingSubscription.options?.applicationServerKey;
        const newKeyBytes = applicationServerKey;
        const existingKeyBytes = existingKey ? new Uint8Array(existingKey as ArrayBuffer) : null;
        const keyChanged = !existingKeyBytes ||
          existingKeyBytes.length !== newKeyBytes.length ||
          existingKeyBytes.some((b, i) => b !== newKeyBytes[i]);

        if (keyChanged) {
          await existingSubscription.unsubscribe();
        } else {
          // Return existing valid subscription
          return existingSubscription;
        }
      }

      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as any
      });

      console.log('Push subscription created:', subscription);

      // Send subscription to backend
      try {
        const subscriptionData = await this.getSubscriptionData();
        if (subscriptionData) {
          await this.sendSubscriptionToBackend(subscriptionData);
          console.log('Subscription sent to backend successfully');
        }
      } catch (backendError) {
        console.error('Failed to send subscription to backend:', backendError);
        // Don't throw here - subscription was created successfully
      }

      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to subscribe to push notifications. Please check browser settings and try again.');
    }
  }

  async unsubscribeFromPushNotifications(): Promise<boolean> {
    if (!this.swRegistration) {
      return false;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        // Get subscription data before unsubscribing
        const subscriptionData = await this.getSubscriptionData();

        // Unsubscribe from push manager
        await subscription.unsubscribe();
        console.log('Push subscription removed');

        // Notify backend about unsubscription
        if (subscriptionData) {
          try {
            await this.removeSubscriptionFromBackend(subscriptionData);
            console.log('Unsubscription sent to backend successfully');
          } catch (backendError) {
            console.error('Failed to notify backend about unsubscription:', backendError);
            // Don't throw here - unsubscription was successful
          }
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  async getCurrentSubscription(): Promise<PushSubscription | null> {
    if (!this.swRegistration) {
      return null;
    }

    try {
      return await this.swRegistration.pushManager.getSubscription();
    } catch (error) {
      console.error('Failed to get current subscription:', error);
      return null;
    }
  }

  async sendTestNotification(): Promise<void> {
    if (!this.swRegistration) {
      throw new Error('Service Worker not registered');
    }

    const permission = await this.getPermissionStatus();
    if (permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    await this.swRegistration.showNotification('Test Notification', {
      body: 'This is a test notification from your PWA',
      icon: '/logo_small.png',
      badge: '/logo_small.png',
      tag: 'test',
      requireInteraction: true
    });
  }

  // Convert VAPID public key from base64 to Uint8Array
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Get subscription data for sending to your backend
  async getSubscriptionData(): Promise<PushSubscriptionData | null> {
    const subscription = await this.getCurrentSubscription();
    if (!subscription) {
      return null;
    }

    const p256dhKey = subscription.getKey('p256dh');
    const authKey = subscription.getKey('auth');

    return {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: p256dhKey ? btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(p256dhKey)))) : '',
        auth: authKey ? btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(authKey)))) : ''
      }
    };
  }

  // Check if the service worker is ready
  async isServiceWorkerReady(): Promise<boolean> {
    if (!this.swRegistration) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      return registration === this.swRegistration;
    } catch (error) {
      return false;
    }
  }

  // Send subscription to backend
  async sendSubscriptionToBackend(subscriptionData: PushSubscriptionData): Promise<void> {
    try {
      await HttpService.post('/push-notifications/subscribe', {
        endpoint: subscriptionData.endpoint,
        auth: subscriptionData.keys.auth,
        p256dh: subscriptionData.keys.p256dh,
        deviceInfo: navigator.userAgent
      });
    } catch (error) {
      console.error('Failed to send subscription to backend:', error);
      throw error;
    }
  }

  // Remove subscription from backend
  async removeSubscriptionFromBackend(subscriptionData: PushSubscriptionData): Promise<void> {
    try {
      await HttpService.post('/push-notifications/unsubscribe', {
        endpoint: subscriptionData.endpoint,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to remove subscription from backend:', error);
      throw error;
    }
  }

  // Legacy method - keeping for backward compatibility
  async subscribeToNotifications(payload: PushSubscription): Promise<void> {
    return HttpService.post(`/push-notifications/subscribe`, payload);
  }

  // Check if subscription exists on backend
  async checkBackendSubscription(endpoint: string): Promise<boolean> {
    try {
      const response = await HttpService.get(`/push-notifications/check/${encodeURIComponent(endpoint)}`);
      return (response as any)?.exists || false;
    } catch (error) {
      console.error('Failed to check backend subscription:', error);
      return false;
    }
  }

  // Retry sending subscription to backend (useful for offline scenarios)
  async retryBackendSync(): Promise<void> {
    try {
      const subscription = await this.getCurrentSubscription();
      if (subscription) {
        const subscriptionData = await this.getSubscriptionData();
        if (subscriptionData) {
          await this.sendSubscriptionToBackend(subscriptionData);
          console.log('Backend sync retry successful');
        }
      }
    } catch (error) {
      console.error('Backend sync retry failed:', error);
      throw error;
    }
  }


}

export const notificationService = new NotificationService();
export default notificationService; 